/**
 * Utility functions for handling USB printer connections
 */

/**
 * Request access to a USB printer device
 */
export const connectToPrinter = async (): Promise<USBDevice | null> => {
  try {
    console.log('Attempting to connect to printer...');
    
    // Check if WebUSB is supported
    if (!navigator.usb) {
      console.error('WebUSB API not supported in this browser');
      throw new Error('WebUSB API not supported in this browser. Please use Chrome, Edge, or Opera.');
    }
    
    // Request a USB device with the appropriate filters for a thermal printer
    // These filters may need to be adjusted based on the specific printer model
    const device = await navigator.usb.requestDevice({
      filters: [
        // Common vendor IDs for thermal printers - expanded list
        { vendorId: 0x0416 }, // Epson
        { vendorId: 0x04b8 }, // Epson (alternative)
        { vendorId: 0x067b }, // Prolific (USB-to-Serial adapter often used with printers)
        { vendorId: 0x0483 }, // Star Micronics
        { vendorId: 0x0dd4 }, // Custom printers
        { vendorId: 0x1504 }, // Bixolon
        { vendorId: 0x154f }, // Xprinter
        { vendorId: 0x1cb0 }, // Rongta
        { vendorId: 0x0519 }, // Star Micronics (alternative)
        { vendorId: 0x0525 }, // PLT
        { vendorId: 0x6868 }, // FTDI (USB-to-Serial)
        { vendorId: 0x0403 }, // FTDI (alternative)
        { vendorId: 0x1a86 }, // QinHeng Electronics
        { vendorId: 0x0fe6 }, // ICP
        { vendorId: 0x0471 }, // Philips
        { vendorId: 0x04e8 }, // Samsung
        { vendorId: 0x20d1 }, // Dascom
        // Allow any device if user explicitly selects it
        // This is needed because some printers might use vendor IDs not in our list
      ],
    });

    console.log('Device selected:', device);
    console.log('Vendor ID:', device.vendorId.toString(16));
    console.log('Product ID:', device.productId.toString(16));
    
    // Open the device
    await device.open();
    console.log('Device opened');
    
    // Find the first output endpoint
    const configuration = device.configurations[0];
    console.log('Selected configuration:', configuration);
    await device.selectConfiguration(configuration.configurationValue);
    
    // Find the interface that has an output endpoint
    const interfaces = configuration.interfaces;
    console.log('Available interfaces:', interfaces);
    
    // Try each interface until we find one that works
    for (let i = 0; i < interfaces.length; i++) {
      const iface = interfaces[i];
      
      try {
        console.log(`Attempting to claim interface ${iface.interfaceNumber}`);
        await device.claimInterface(iface.interfaceNumber);
        console.log(`Successfully claimed interface ${iface.interfaceNumber}`);
        return device;
      } catch (err) {
        console.warn(`Failed to claim interface ${iface.interfaceNumber}:`, err);
        // Continue to the next interface
      }
    }
    
    throw new Error('Could not claim any interface with output endpoint');
  } catch (error) {
    console.error('Error connecting to printer:', error);
    return null;
  }
};

/**
 * Send data to a connected USB printer
 */
export const sendToPrinter = async (
  device: USBDevice,
  data: Uint8Array
): Promise<boolean> => {
  try {
    console.log('Attempting to send data to printer...');
    console.log('Data length:', data.length, 'bytes');
    
    // Find the first output endpoint
    const configuration = device.configurations[0];
    
    // Find all interfaces with output endpoints
    const interfaces = configuration.interfaces.filter(
      iface => iface.alternates.some(
        alt => alt.endpoints.some(
          endpoint => endpoint.direction === 'out'
        )
      )
    );
    
    if (interfaces.length === 0) {
      throw new Error('No interface with output endpoint found');
    }
    
    console.log(`Found ${interfaces.length} interface(s) with output endpoints`);
    
    // Try each interface until we find one that works
    for (const iface of interfaces) {
      try {
        // Find the alternate setting with output endpoint
        const alternate = iface.alternates.find(
          alt => alt.endpoints.some(
            endpoint => endpoint.direction === 'out'
          )
        );
        
        if (!alternate) {
          console.warn(`Interface ${iface.interfaceNumber} has no alternate with output endpoint`);
          continue;
        }
        
        // Find all output endpoints
        const endpoints = alternate.endpoints.filter(
          endpoint => endpoint.direction === 'out'
        );
        
        if (endpoints.length === 0) {
          console.warn(`Interface ${iface.interfaceNumber} has no output endpoints`);
          continue;
        }
        
        console.log(`Interface ${iface.interfaceNumber} has ${endpoints.length} output endpoint(s)`);
        
        // Try each endpoint until we find one that works
        for (const endpoint of endpoints) {
          try {
            console.log(`Attempting to send data to endpoint ${endpoint.endpointNumber}`);
            
            // Send the data to the printer in chunks to avoid buffer issues
            const CHUNK_SIZE = 64; // Standard USB packet size
            
            for (let i = 0; i < data.length; i += CHUNK_SIZE) {
              const chunk = data.slice(i, Math.min(i + CHUNK_SIZE, data.length));
              const result = await device.transferOut(endpoint.endpointNumber, chunk);
              console.log(`Sent chunk ${i/CHUNK_SIZE + 1}/${Math.ceil(data.length/CHUNK_SIZE)}, bytes written:`, result.bytesWritten);
            }
            
            console.log('Data sent successfully');
            return true;
          } catch (err) {
            console.warn(`Failed to send data to endpoint ${endpoint.endpointNumber}:`, err);
            // Continue to the next endpoint
          }
        }
      } catch (err) {
        console.warn(`Error with interface ${iface.interfaceNumber}:`, err);
        // Continue to the next interface
      }
    }
    
    throw new Error('Failed to send data to any output endpoint');
  } catch (error) {
    console.error('Error sending data to printer:', error);
    return false;
  }
};

/**
 * Disconnect from a USB printer
 */
export const disconnectPrinter = async (device: USBDevice): Promise<boolean> => {
  try {
    console.log('Disconnecting from printer...');
    await device.close();
    console.log('Disconnected successfully');
    return true;
  } catch (error) {
    console.error('Error disconnecting from printer:', error);
    return false;
  }
};

/**
 * List all available USB devices (for debugging)
 */
export const listUSBDevices = async (): Promise<USBDevice[]> => {
  try {
    if (!navigator.usb) {
      throw new Error('WebUSB API not supported in this browser');
    }
    
    const devices = await navigator.usb.getDevices();
    console.log('Available USB devices:', devices);
    return devices;
  } catch (error) {
    console.error('Error listing USB devices:', error);
    return [];
  }
};
